import classNames from "classnames";
import { match } from "ts-pattern";
import { NumberPill } from "../pills";

type Tab<T extends string> = {
  id: T;
  label: string;
  counter?: number;
};

type Theme = "office" | "mobile";

type Props<T extends string> = {
  tabs: Tab<T>[];
  currentTab: T;
  onChange: (tab: T) => void;
  theme?: Theme;
  variant?: "default" | "leading";
};

export const TabSection = <T extends string>({
  tabs,
  currentTab,
  onChange,
  theme = "office",
  variant = "default",
}: Props<T>) => {
  const isMobileTheme = theme === "mobile";

  return (
    <div
      className={classNames(
        "relative w-full border-b",
        match({ theme, variant })
          .with(
            { theme: "mobile", variant: "leading" },
            () => "pl-3 border-greyscale-700",
          )
          .with({ theme: "mobile" }, () => "border-greyscale-700")
          .otherwise(() => "border-greyscale-200"),
      )}
    >
      <div
        className={classNames(
          "flex",
          match({ theme, variant })
            .with(
              { theme: "mobile", variant: "leading" },
              () => "w-full justify-start gap-1",
            )
            .with({ theme: "mobile" }, () => "w-full justify-between px-6")
            .otherwise(() => "inline-flex"),
        )}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === currentTab;

          return (
            <button
              key={tab.id}
              className={classNames(
                "relative flex items-center justify-center gap-2 bg-transparent font-medium px-4 py-2 text-sm h-10 transition-colors duration-200 font-poppins",
                {
                  // Unselected tab
                  "text-greyscale-700 dark:text-greyscale-300 hover:text-primary-700 dark:hover:text-primary-300":
                    !isActive,

                  // Selected tab
                  "text-primary-700 dark:text-primary-300": isActive,

                  // Mobile theme: buttons take only needed space, centered in container
                  "justify-center": isMobileTheme,
                },
              )}
              onClick={() => onChange(tab.id)}
              type="button"
              aria-current={isActive ? "page" : undefined}
            >
              <span>{tab.label}</span>
              <NumberPill count={tab.counter ? tab.counter : 0} color="blue" />

              {isActive && (
                <span
                  className={classNames(
                    "absolute bottom-[-1px] left-0 right-0",
                    {
                      "h-[1px] bg-primary-600": !isMobileTheme,
                      "h-[2px] bg-primary-300": isMobileTheme,
                    },
                  )}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
